import type { WhiteboardLayout } from '../core/WhiteboardLayout';
import { ArrowModes, LineStyles, type ArrowMode, type LineStyle } from '../net/wire';

/**
 * What the dominant hand is doing: **build**, an empty hand placing and grabbing nodes, or
 * **draw**, holding ink against the whiteboard. Pens and the eraser are no longer world
 * objects — no ledge, no mesh, nothing to reach for or clone — so this is the entire
 * replacement for `PenLedge`'s state: "which ink is selected" and "is a pen in hand" now live
 * here, as plain fields, not as a physical prop attached to a grabber.
 *
 * Why here and not somewhere else: the owner's headset verdict was that the *tray* mechanic
 * for reaching a pen — a shelf you walk to, a prop you clone onto a hand — was the redundant
 * part once the dominant stick can cycle tools directly ("when you have the switching on the
 * right stick there is just no point to it"). Nothing about that verdict says the *state* was
 * wrong, only the delivery. So the state survives, stripped of every Scene/Mesh/GrabberHub
 * dependency it used to need to carry a prop around — this class knows nothing about Babylon
 * and needs no `.start()`/`.dispose()`, because there is no per-frame tracking left to do: no
 * mesh to reposition onto an aim pose, no reach to test, no clone to dispose. It is exactly as
 * much state as the two questions above require, and no more.
 *
 * `plan/history/control-modes.md`'s own opening claim — "the mode is not abstract state, it is what is
 * in your dominant hand" — is worth being honest about here rather than quietly dropped: with
 * no held prop to look at, the mode genuinely is what that document argued against, an
 * invisible flag. What is left of the original argument is that the *tray* still reflects it
 * immediately (ink colours instead of shapes the instant `mode` flips), and a controller's own
 * stick position is a physical, felt state the way a click was not — but neither is "look at
 * your hand," and CLAUDE.md records this honestly rather than the document quietly losing the
 * paragraph.
 */

export type Mode = 'build' | 'draw' | 'scan' | 'link';

export type PenKind = 'pen' | 'eraser';

export interface PenSelection {
  readonly kind: PenKind;
  readonly color: string;
}

/**
 * Marker thickness, three fixed steps rather than a free-form number — mirrors
 * `Tunet.Contracts.Dtos.StrokeWidths`/`net/wire.ts`'s own `StrokeWidths`. `'medium'` is what
 * shipped before this existed, so it stays the default: an owner who never touches the
 * dominant stick's Y axis draws exactly the strokes they always did.
 *
 * This is a *persisted stroke* vocabulary — `MarkerWidth` mirrors `StrokeWidths` one-for-one,
 * and a stroke's own `width` field on the wire has to be one of these three values. The eraser
 * is not a width and must never be added here (plan/mode-hierarchy.md's own draw-variant task:
 * "the eraser is not a width. Do not add it there") — see `DrawVariant`, below, for where the
 * eraser actually joins the same *axis* without joining this *vocabulary*.
 */
export type MarkerWidth = 'fine' | 'medium' | 'fat';

/**
 * The dominant stick's ↕ axis in draw mode, four discrete positions — plan/mode-hierarchy.md,
 * "Draw gets four discrete tool variants; the eraser stops being a button anywhere": slim,
 * medium and fat markers, then the eraser, all on one ordered list. Before this, "which marker
 * width" (`MarkerWidth`, three steps) and "pen or eraser" (`PenSelection.kind`, toggled only
 * through `selectInk`, with no stick-axis entry point at all once the ink wheel that used to
 * offer the eraser was deleted alongside the rest of `1daa623`'s mode-hierarchy rework) were two
 * independent ideas with two independent mechanisms. Folding the eraser into this same ordered
 * list — rather than keeping a second, parallel "toggle the eraser" gesture beside the width
 * cycle — is what the task instructs directly: "Four positions on one axis means those two
 * ideas become one ordered list... Do not leave both mechanisms alive in parallel." A
 * `DrawVariant` is exactly `MarkerWidth` widened by one member; nothing about the persisted
 * `MarkerWidth` vocabulary itself changes; a `MarkerWidth` is always a valid `DrawVariant`.
 */
export type DrawVariant = MarkerWidth | 'eraser';

/** The positions the dominant stick's ↕ axis in draw mode cycles through, in order — see
 *  `cycleDrawVariant`. The eraser sits last, after the three marker widths, so cycling forward
 *  from the default (`'medium'`) reaches it in two steps and cycling backward reaches it in one —
 *  either direction reaches every position, since the cycle wraps. */
const DRAW_VARIANTS: readonly DrawVariant[] = ['fine', 'medium', 'fat', 'eraser'];

/**
 * The modes the mode-switch gesture cycles through, in order. Written as a list and stepped
 * through with wrapping arithmetic — not an `if` toggling a boolean — because the whole point
 * of a flick-cycle over a click is room for more than two (control-modes.md's own "More than
 * two modes" was rejected only for lack of a third tool at the time, not because the mechanism
 * could not hold one). `scan` reads the handwriting inside a swept X range and redraws it
 * cleanly (`plan/whiteboard.md`, "The scan tool"; the gesture itself lives in `ScanTool.ts`,
 * which — like `DrawingManager` for `draw` — is the only thing that reads `mode === 'scan'` to
 * actually do anything; this class only ever tracks which mode is current).
 *
 * `link` is the fourth mode plan/mode-hierarchy.md settles on; the mode-switch gesture reaches
 * it the same as any other position, and its own linking gesture is implemented below (see this
 * file's own "Link mode" section, `fca7ade`).
 */
const MODES: readonly Mode[] = ['build', 'draw', 'scan', 'link'];

// spec: SPEC-190
/**
 * The two modes that exist purely to act on the whiteboard — `draw` puts ink on it, `scan` reads
 * handwriting off it (`ScanTool.tick`'s own `if (!grabber || !whiteboard || !active)` early
 * return: with no whiteboard, scan does nothing at all, the identical shape draw's own selection
 * degrades to with `penColors` empty). SPEC-190 ("a whiteboard is not a fixture every room
 * arrives with," plan/lobby-v1.md) means every real room now constructs `ToolMode` with a `null`
 * whiteboard (`SceneRoot.whiteboard`'s own doc comment) — a mode that can never do anything
 * without a board is worse than a mode that is not offered (CLAUDE.md: "a mode that sits in the
 * mode ring, selectable, and then does nothing is worse than a mode that is not offered"), so both
 * are dropped from the cycle together, for the identical reason, rather than only `draw`. This is
 * an implementer's judgement call the brief left open ("handle or explicitly report it") — the
 * `whiteboard`-typed constructor parameter itself, and everything `selection`/`penColors`/
 * `eraserSelection` already do with a `null` one, are untouched; only which modes `cycleTool`
 * can ever land on changes.
 */
const BOARD_DEPENDENT_MODES: readonly Mode[] = ['draw', 'scan'];

export class ToolMode {
  private currentMode: Mode = 'build';

  /**
   * The modes `cycleTool` actually cycles through — `MODES` itself with `BOARD_DEPENDENT_MODES`
   * removed when this room has no whiteboard (`BOARD_DEPENDENT_MODES`'s own doc comment). Fixed
   * for this instance's whole lifetime, the same way `whiteboard` itself is: nothing rebuilds a
   * `ToolMode` mid-room, only `SceneRoot.buildRoom`'s own dispatch, from before this class ever
   * sees a whiteboard.
   */
  private readonly cyclableModes: readonly Mode[];

  /** Which position on the draw-variant axis is current — a marker width, or the eraser. The
   *  single source of truth `selection`/`markerWidth` both derive from, rather than two
   *  independently-settable fields that could disagree (see `DrawVariant`'s own doc). */
  private currentVariant: DrawVariant = 'medium';

  /** The pen's own colour, independent of `currentVariant` — deliberately never cleared by
   *  switching to the eraser, so cycling past the eraser and back to a marker restores the same
   *  colour without this class having to remember it a second way. Null only with no whiteboard
   *  in the room (`penColors` is then empty and there is nothing to default to). */
  private currentPenColor: string | null;

  constructor(private readonly whiteboard: WhiteboardLayout | null) {
    const firstColor = whiteboard?.penColors[0];
    this.currentPenColor = firstColor ?? null;
    this.cyclableModes = whiteboard
      ? MODES
      : MODES.filter((mode) => !BOARD_DEPENDENT_MODES.includes(mode));
  }

  get mode(): Mode {
    return this.currentMode;
  }

  /** Every mode `cycleTool` can actually land on, in cycle order — `ModeRingRenderer`'s own
   *  source of which plates to show, rather than a second, independently-maintained list that
   *  could disagree with what pressing the stick actually reaches. See `BOARD_DEPENDENT_MODES`'s
   *  own doc comment for why this is shorter than `Mode`'s own four values with no whiteboard in
   *  the room. */
  get availableModes(): readonly Mode[] {
    return this.cyclableModes;
  }

  get isDrawMode(): boolean {
    return this.currentMode === 'draw';
  }

  get isScanMode(): boolean {
    return this.currentMode === 'scan';
  }

  get isLinkMode(): boolean {
    return this.currentMode === 'link';
  }

  /**
   * Either non-build tool. `PossessionManager.considerGrab` and `ConnectionDrawer.tick` both
   * used to read `isDrawMode` alone to decide whether the dominant hand's grip/trigger is
   * "reserved" for the current tool rather than free for a grab or a connection — the scan
   * tool reserves the trigger the identical way (CLAUDE.md, "Gestures are arbitrated in one
   * place": pressing it sets one edge of the scanned range, sweeping and releasing sets the
   * other), so both guards read this instead of `isDrawMode` on its own now. Kept as a
   * separate getter rather than folding scan into `isDrawMode` itself: `draw`'s own
   * ink-selection/tray-context behaviour is genuinely draw-specific and must not fire for scan.
   *
   * `link` reserves the trigger identically — its own gesture is implemented (this file's
   * "Link mode" section below, `fca7ade`) and `ConnectionDrawer.tick` is what actually drives
   * it while this mode is current.
   */
  get isToolActive(): boolean {
    return this.currentMode !== 'build';
  }

  /**
   * Whichever pen or the eraser is currently selected — the ink a stroke would be drawn with,
   * or erase with, right now. Derived from `currentVariant`/`currentPenColor` rather than a
   * field of its own, so the eraser and the three marker widths can never disagree about which
   * one is actually current the way two independently-settable fields could. Not null'd on
   * leaving draw mode: switching back to build and then back to draw restores the same colour,
   * the same "whichever pen you last chose" persistence `PenLedge.lastSelection` used to give
   * across a put-away.
   */
  get selection(): PenSelection | null {
    if (this.currentVariant === 'eraser') {
      return this.eraserSelection;
    }

    return this.currentPenColor ? { kind: 'pen', color: this.currentPenColor } : null;
  }

  /** The draw-variant axis's own current position — a marker width, or the eraser. Exposed
   *  separately from `markerWidth` (which only ever answers "what would a *marker* stroke's
   *  width be," `'medium'` while erasing) because a caller stepping through every position on
   *  the axis — the dominant stick's ↕ binding, and tests proving it reaches all four — needs to
   *  tell "genuinely at the eraser" apart from "at the default marker width," which `markerWidth`
   *  alone cannot distinguish. */
  get variant(): DrawVariant {
    return this.currentVariant;
  }

  /**
   * The eraser's own selection, as it actually exists on this whiteboard — looked up rather
   * than a hard-coded colour, the same reason `PenLedge.eraserSelection` existed: a caller (the
   * tray's ink wheel) never has to know the eraser's colour constant to offer it. Null only
   * when there is no whiteboard in this room.
   */
  get eraserSelection(): PenSelection | null {
    return this.whiteboard ? { kind: 'eraser', color: this.whiteboard.eraserColor } : null;
  }

  /** Every pen colour this whiteboard offers, in ledge order. Empty with no whiteboard. */
  get penColors(): readonly string[] {
    return this.whiteboard?.penColors ?? [];
  }

  /**
   * Advances the mode by `delta`, wrapping — the mode-switch gesture (dominant grip + stick
   * left/right, `AuthoringController.tickModeSwitch`; plan/mode-hierarchy.md, "Mode switching
   * is dominant grip + stick left/right") and the desktop's `p` key alike (`DevSim.ts` has no
   * grip, so it cycles the same way `cycleTool`'s own predecessor did before the redesign — see
   * plan/mode-hierarchy.md, "Desktop — open question, not a decision"). Kept the name
   * `cycleTool` rather than renaming to `cycleMode`: every existing caller
   * (`AuthoringController.cycleTool`, `DevSim`'s `p` key) already reads it that way, and this
   * class's own `Mode` type is still what the codebase at large calls "the tool."
   *
   * Steps through `this.cyclableModes`, not the full four-value `MODES` — with no whiteboard in
   * the room, `draw` and `scan` are absent from the list entirely (`BOARD_DEPENDENT_MODES`'s own
   * doc comment) rather than present and silently doing nothing once reached. If `currentMode`
   * itself is not in `cyclableModes` (unreachable in practice — nothing ever calls
   * `enterDrawMode`/`enterScanMode` outside a test that also constructs a whiteboard, per those
   * methods' own doc comments), `indexOf` returns `-1` and this steps from just before the start
   * of the list rather than throwing.
   */
  cycleTool(delta: 1 | -1): void {
    const modes = this.cyclableModes;
    const index = modes.indexOf(this.currentMode);
    this.currentMode = modes[(index + delta + modes.length) % modes.length]!;
  }

  enterBuildMode(): void {
    this.currentMode = 'build';
  }

  enterDrawMode(): void {
    this.currentMode = 'draw';
  }

  /** Direct setter, the same shape as `enterDrawMode`/`enterScanMode` — nothing in the real
   *  control scheme calls this today (link is only ever reached by the mode-switch gesture),
   *  but tests reach for it the same way. */
  enterLinkMode(): void {
    this.currentMode = 'link';
  }

  /** Direct setter, the same shape as `enterDrawMode`/`enterBuildMode` — nothing in the real
   *  control scheme calls this today (scan is only ever reached by cycling the dominant
   *  stick), but tests reach for it the same way they reach for `enterDrawMode` rather than
   *  driving `cycleTool` through every intermediate step. */
  enterScanMode(): void {
    this.currentMode = 'scan';
  }

  /**
   * Sets which pen or the eraser is selected directly — a test's own shortcut, and (historically)
   * the ink wheel's own live-spin call before that wheel was deleted alongside the rest of
   * `1daa623`'s mode-hierarchy rework. Does not itself change `mode`.
   *
   * Selecting the eraser moves `currentVariant` to `'eraser'` without touching `currentPenColor`
   * at all — the colour is remembered independently precisely so this is reversible. Selecting a
   * pen colour sets `currentPenColor` and, if the eraser was current, moves `currentVariant` back
   * to `'medium'` — a deliberate, explicit pick of an ink is exactly the kind of act that should
   * leave the eraser, the same way choosing a slot from the tray always committed to something
   * new rather than leaving old state to disagree with it.
   */
  selectInk(selection: PenSelection): void {
    if (selection.kind === 'eraser') {
      this.currentVariant = 'eraser';
      return;
    }

    this.currentPenColor = selection.color;

    if (this.currentVariant === 'eraser') {
      this.currentVariant = 'medium';
    }
  }

  /**
   * Steps `selection` through the pen colours by `delta`, wrapping — the dominant hand's own
   * bare-stick ↔ axis in draw mode (`ModeDefinitions.ts`). Applies live, the same "no separate
   * commit" rule the off-hand tray's own ink wheel already followed before it was deleted
   * (plan/tray-categories.md, "the wheel carries settings") — there is nothing here to commit on
   * release, only a stick that keeps being read every tick (`AuthoringController.tickToolCycle`).
   *
   * A no-op while the eraser is selected — plan/mode-hierarchy.md's own call: "with the eraser
   * selected, colour has nothing to act on... a live-looking colour control that silently does
   * nothing when the eraser is selected would be worse than showing no control at all." Colour
   * genuinely has nothing to apply to while erasing, so this leaves `currentPenColor` (and
   * `currentVariant`) completely untouched rather than picking some marker colour on the
   * eraser's behalf — the caller (`ModeDefinitions.ts`'s own stickX binding) reads `selection`
   * afterward and shows no change, which is only honest if nothing actually changed here. Also a
   * no-op with no pen colours to offer at all (no whiteboard in the room).
   */
  cyclePenColor(delta: 1 | -1): void {
    if (this.currentVariant === 'eraser') {
      return;
    }

    const colors = this.penColors;

    if (colors.length === 0) {
      return;
    }

    const currentIndex = this.currentPenColor ? colors.indexOf(this.currentPenColor) : -1;
    const baseIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = currentIndex === -1 ? 0 : (baseIndex + delta + colors.length) % colors.length;

    this.currentPenColor = colors[nextIndex]!;
  }

  /**
   * What a *marker* stroke's width would be right now — `'medium'` while the eraser is selected,
   * since the eraser has no width of its own and nothing reads this value in that state anyway
   * (`DrawingManager.beginStroke` only ever reads it on the `selection?.kind === 'pen'` branch;
   * `ToolPropRenderer.ensurePenOrEraserMesh` ignores its `width` argument entirely once
   * `selection.kind !== 'pen'`). Use `variant` instead of this getter anywhere the eraser itself
   * has to be told apart from the marker default — see that getter's own doc.
   */
  get markerWidth(): MarkerWidth {
    return this.currentVariant === 'eraser' ? 'medium' : this.currentVariant;
  }

  /**
   * Advances the draw-variant axis by `delta`, wrapping through all four positions —
   * `DRAW_VARIANTS`'s own order, `fine -> medium -> fat -> eraser -> fine -> ...` — the dominant
   * stick's up/down flick in draw mode (`ModeDefinitions.ts`). Replaces the old `cycleMarkerWidth`
   * (three steps, no eraser): the eraser used to be reachable only through `selectInk`, with no
   * stick-axis entry point once the ink wheel that offered it was deleted — this is what makes it
   * reachable again, as the fourth position on the same axis rather than a second, parallel
   * gesture (plan/mode-hierarchy.md, "the eraser stops being a button anywhere").
   */
  cycleDrawVariant(delta: 1 | -1): void {
    const index = DRAW_VARIANTS.indexOf(this.currentVariant);
    this.currentVariant = DRAW_VARIANTS[(index + delta + DRAW_VARIANTS.length) % DRAW_VARIANTS.length]!;
  }

  // --- Link mode ------------------------------------------------------------------------------
  //
  // plan/mode-hierarchy.md settles Link's own bindings: dominant stick left/right cycles the
  // line style a link is drawn (or re-drawn) with, including a "remove" position that is *not*
  // a persisted style — re-linking two already-connected nodes while it is selected deletes
  // that connection instead of restyling it (`ConnectionStyleDto.cs`'s own `LineStyles` remarks
  // say the same thing from the contracts side: "that is a UI action, not a persisted line
  // style"). Dominant stick up/down cycles the arrow direction. Both read real wire vocabulary
  // (`net/wire.ts`'s `LineStyles`/`ArrowModes`) rather than a hand-written label list — the
  // placeholder that used to live here predates real linking and is gone now that
  // `ConnectionDrawer` actually reads these values to create, restyle or remove a connection.

  /** `'remove'` is deliberately not a `LineStyle` — see `ConnectionStyleDto.cs`'s own remarks, and
   *  plan/mode-hierarchy.md's "Do not add that 'none' position to `LineStyles` itself." It is a
   *  UI-only selector value `ConnectionDrawer` reads to choose "delete" over "create/restyle"
   *  when a drag lands on an already-connected pair; it must never reach the wire as a style. */
  private static readonly LINK_LINE_SELECTIONS: readonly (LineStyle | 'remove')[] = [
    LineStyles.Solid,
    LineStyles.Dashed,
    LineStyles.Dotted,
    'remove',
  ];

  /** Forward first, matching `ArrowModes.Default` (`ConnectionStyleDto.cs`) — a fresh session's
   *  Link mode offers the same default a brand-new edge would already have gotten without ever
   *  touching this axis. */
  private static readonly LINK_ARROW_MODES: readonly ArrowMode[] = [
    ArrowModes.Forward,
    ArrowModes.Both,
    ArrowModes.None,
  ];

  private currentLinkLineIndex = 0;
  private currentLinkArrowIndex = 0;

  /** The line style (or `'remove'`) a link drawn or re-drawn right now would use — read by
   *  `ConnectionDrawer.submit` on every completed drag. */
  get linkLineSelection(): LineStyle | 'remove' {
    return ToolMode.LINK_LINE_SELECTIONS[this.currentLinkLineIndex]!;
  }

  /** The arrow mode a link drawn or re-drawn right now would use. Meaningless while
   *  `linkLineSelection` is `'remove'` — `ConnectionDrawer` never reads this in that case,
   *  the same "nothing to act on" shape `draw` mode's own colour axis has with the eraser
   *  selected (plan/mode-hierarchy.md). */
  get linkArrowMode(): ArrowMode {
    return ToolMode.LINK_ARROW_MODES[this.currentLinkArrowIndex]!;
  }

  /** Prose for the off-hand readout — the value itself, since there is nothing more to say
   *  about a line style than its own name (`'remove'` included). */
  get linkStyleLabel(): string {
    return this.linkLineSelection;
  }

  /**
   * `'n/a'` while `linkLineSelection` is `'remove'` — choosing a direction for a link you are
   * about to delete is meaningless, so the readout says so plainly rather than showing the last
   * real arrow mode as though it still meant something (`edb8a79`'s own precedent for the colour
   * axis while the eraser is selected: "a live-looking colour control that silently does nothing
   * ... would be worse than showing no control at all," plan/mode-hierarchy.md). The underlying
   * `linkArrowMode` index is untouched by this — `ConnectionDrawer` never reads it while removing
   * a connection anyway (this getter's own sibling doc, above).
   */
  get linkArrowLabel(): string {
    return this.linkLineSelection === 'remove' ? 'n/a' : this.linkArrowMode;
  }

  cycleLinkStyle(delta: 1 | -1): void {
    const options = ToolMode.LINK_LINE_SELECTIONS;
    this.currentLinkLineIndex = (this.currentLinkLineIndex + delta + options.length) % options.length;
  }

  /**
   * A no-op while `linkLineSelection` is `'remove'` — the same guard `cyclePenColor` applies
   * while the eraser is selected, and for the identical reason: the axis this steps has nothing
   * to act on. Re-linking two nodes with the style cycled to "remove" deletes the connection
   * (`ConnectionDrawer.submit`), and an arrow direction for a link about to be deleted is
   * meaningless — locking the axis here, not just labelling it inert (`linkArrowLabel`, above),
   * is what stops a stray stick push from quietly changing a value nobody can see take effect.
   */
  cycleLinkArrow(delta: 1 | -1): void {
    if (this.linkLineSelection === 'remove') {
      return;
    }

    const options = ToolMode.LINK_ARROW_MODES;
    this.currentLinkArrowIndex = (this.currentLinkArrowIndex + delta + options.length) % options.length;
  }
}
